import { transport, useTransportMutation } from "@/transport"

import type { Annotation } from "@/types/api/Annotation"

import { mapReviewAnnotationResource } from "./reviewAnnotationQueriesTransport"

interface CreateReviewAnnotationInput {
  simulationOutputId: string
  metricId: string
  assignee: string
  ground_truth_float_value?: number | null
  ground_truth_string_value?: string | null
  ground_truth_set_value?: string[] | null
  ground_truth_subvalues_by_timestamp?: unknown[] | null
  ground_truth_json?: Record<string, unknown> | null
  annotations?: Annotation["annotations"]
  projectId?: string | null
}

interface UpdateReviewAnnotationInput {
  annotationId: string
  groundTruthFloatValue?: number | null
  groundTruthStringValue?: string | null
  groundTruthSetValue?: string[] | null
  groundTruthSubvaluesByTimestamp?: unknown[] | null
  groundTruthJson?: Record<string, unknown> | null
  annotations?: Annotation["annotations"]
  priority?: Annotation["priority"]
  assignee?: string | null
  projectId?: string | null
}

interface ReviewAnnotationMutationOptions {
  onSuccess?: (annotation: Annotation) => void
  onError?: (error: unknown) => void
}

interface DeleteReviewAnnotationMutationOptions {
  onSuccess?: () => void
  onError?: (error: unknown) => void
}

export function useCreateReviewAnnotationTransport(
  options?: ReviewAnnotationMutationOptions,
) {
  const mutation = useTransportMutation(
    transport.operations.createReviewAnnotation,
    {
      onSuccess: (response) => {
        options?.onSuccess?.(
          mapReviewAnnotationResource(response.review_annotation),
        )
      },
      onError: options?.onError,
    },
  )

  return {
    ...mutation,
    mutateAsync: async (input: CreateReviewAnnotationInput) => {
      const response = await mutation.mutateAsync({
        simulation_output_id: input.simulationOutputId,
        metric_id: input.metricId,
        assignee: input.assignee,
        ground_truth_float_value: input.ground_truth_float_value,
        ground_truth_string_value: input.ground_truth_string_value,
        ground_truth_set_value: input.ground_truth_set_value,
        ground_truth_subvalues_by_timestamp:
          input.ground_truth_subvalues_by_timestamp,
        ground_truth_json: input.ground_truth_json,
        annotations: input.annotations,
        project_id: input.projectId,
      })

      return mapReviewAnnotationResource(response.review_annotation)
    },
  }
}

export function useUpdateReviewAnnotationTransport(
  options?: ReviewAnnotationMutationOptions,
) {
  const mutation = useTransportMutation(
    transport.operations.updateReviewAnnotation,
    {
      onSuccess: (response) => {
        options?.onSuccess?.(
          mapReviewAnnotationResource(response.review_annotation),
        )
      },
      onError: options?.onError,
    },
  )

  return {
    ...mutation,
    mutateAsync: async (input: UpdateReviewAnnotationInput) => {
      const request = {
        ground_truth_float_value: input.groundTruthFloatValue,
        ground_truth_string_value: input.groundTruthStringValue,
        ground_truth_set_value: input.groundTruthSetValue,
        ground_truth_subvalues_by_timestamp:
          input.groundTruthSubvaluesByTimestamp,
        ground_truth_json: input.groundTruthJson,
        annotations: input.annotations,
        priority:
          input.priority === undefined
            ? undefined
            : input.priority === "PRIMARY"
              ? "PRIORITY_PRIMARY"
              : "PRIORITY_STANDARD",
        assignee: input.assignee,
      }
      const response = await mutation.mutateAsync({
        ...request,
        annotation_id: input.annotationId,
        project_id: input.projectId,
      })

      return mapReviewAnnotationResource(response.review_annotation)
    },
  }
}

export function useDeleteReviewAnnotationTransport(
  options?: DeleteReviewAnnotationMutationOptions,
) {
  const mutation = useTransportMutation(
    transport.operations.deleteReviewAnnotation,
    {
      onSuccess: () => {
        options?.onSuccess?.()
      },
      onError: options?.onError,
    },
  )

  return {
    ...mutation,
    mutateAsync: (input: { annotationId: string }) => {
      return mutation.mutateAsync({ annotation_id: input.annotationId })
    },
  }
}
